import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbDateAdapter, NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { ApiCustomerService } from '@services/api-customer.service';
import { FormBase } from '@shared/form-base.component';
import { RegisterModel, RegisterVerificationModel } from '@shared/models/RegisterModel';
import { Country, IdCardType } from '@shared/models/Enums';
import { GeoDataStructure, IGeoDataStructure } from '@shared/geo-data';
import { NgbDateStringAdapter, NgbDateStringParserFormatter } from '@shared/ngb-date-string-adapter';

/**
 * Formulario genérico de registro de cuenta completo (email, nombre, apellido,
 * fecha de nacimiento, género, identificación, provincia, cantón). No pide
 * password: el login se maneja luego por código.
 *
 * Reutilizable en `checkout-plan`, `public-scratch` y cualquier otro flujo
 * donde se necesite crear una cuenta con todos los datos sin verificación por
 * OTP. Es un clon de `checkout-plan-register` extraído a components para poder
 * usarlo en múltiples módulos vía `shared.module`.
 */
@Component({
    selector: 'app-account-register-form',
    templateUrl: './account-register-form.component.html',
    styleUrls: ['./account-register-form.component.scss'],
    // El datepicker trabaja sobre el mismo string 'DD/MM/AAAA' que ya usaba el input.
    providers: [
        { provide: NgbDateAdapter, useClass: NgbDateStringAdapter },
        { provide: NgbDateParserFormatter, useClass: NgbDateStringParserFormatter },
    ]
})
export class AccountRegisterFormComponent extends FormBase<RegisterModel> implements OnInit, OnChanges {

    @Input() country: Country = Country.CR;
    /** Email inicial (útil cuando el cliente existe pero le falta perfil). */
    @Input() initialEmail: string = null;
    /** Si es false, oculta el input de email (ya lo tenemos del cliente). */
    @Input() showEmail: boolean = true;
    /**
     * Modo reducido: solo pide email, nombre y apellido. Se usa en cupones
     * gratis (normales y de comunidad) y en raspaditas. En este modo no se
     * arman ni piden identificación, fecha de nacimiento, género ni geo.
     */
    @Input() minimal: boolean = false;
    /** Si es false, oculta nombre y apellido (los aporta la cuenta de Google). */
    @Input() showName: boolean = true;
    /** Si es false, no se pide identificación (cupones con Google). */
    @Input() showIdCard: boolean = true;
    /** Valores conocidos de la cuenta para precargar el formulario. */
    @Input() prefill: any = null;

    @Output() onSendRegisterInfo = new EventEmitter<RegisterModel>();

    registerForm: UntypedFormGroup;

    emailDuplicated = false;

    /** Último correo consultado, para no repetir la verificación. */
    private _lastCheckedEmail: string = null;

    geoDataStructure: IGeoDataStructure[] = [];
    geoData: any;
    filteredData: any = {};

    /** Rango del calendario de nacimiento: de 1920 hasta hoy. */
    minBirthDate: NgbDateStruct = { year: 1920, month: 1, day: 1 };
    maxBirthDate: NgbDateStruct;
    startBirthDate: NgbDateStruct = { year: 1995, month: 1, day: 1 };

    constructor(private formBuilder: UntypedFormBuilder,
        private apiCustomerService: ApiCustomerService,
    ) {
        super();
    }

    ngOnInit(): void {

        const today = new Date();
        this.maxBirthDate = { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() };

        // En modo mínimo no cargamos la data geográfica: no se piden provincia/cantón.
        if (!this.minimal) this._geoInit();

        this._formInit();
    }

    /**
     * El país lo manda el teléfono, así que puede cambiar después de armado el form.
     * Se recarga la geo y se resetean provincia/cantón: los ids de un país no valen
     * para el otro.
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (!changes.country || changes.country.firstChange) return;
        if (this.minimal || !this.registerForm) return;

        const previousKeys = this.geoDataStructure.map(field => field.key);

        this._geoInit();

        previousKeys
            .filter(key => !this.geoDataStructure.some(field => field.key === key))
            .forEach(key => this.registerForm.removeControl(key));

        this.geoDataStructure.forEach((field) => {

            if (this.registerForm.get(field.key)) this.registerForm.get(field.key).setValue(0);
            else this.registerForm.addControl(field.key, this._geoControl());
        });
    }

    private _geoInit(): void {

        this.geoDataStructure = GeoDataStructure.country(this.country) || [];
        this.geoData = GeoDataStructure.countryData(this.country) || {};
        this.filteredData = {};

        this.geoDataStructure.forEach((field, index) => {

            this.filteredData[field.key] = index === 0 ? this.geoData[field.key] : [this.geoData[field.key][0]];
        });
    }

    private _geoControl(): UntypedFormControl {
        return new UntypedFormControl(0, [Validators.required, Validators.min(1)]);
    }

    onCheckEmail() {

        if (this.invalidEmail) return;

        const email = this.email.value.toLowerCase().trim();

        // (input) y (blur) llaman a esto mismo. Al tocar el botón, el blur volvía a
        // emitir null y el click salía con registerInfo en null: había que hacer
        // clic dos veces. Si el correo no cambió, no hay nada que verificar.
        if (email === this._lastCheckedEmail) return;

        this._lastCheckedEmail = email;
        this.emailDuplicated = false;
        // Al cambiar el email invalidamos temporalmente el form al padre, para que
        // el boton no siga habilitado con la respuesta anterior.
        this.onSendRegisterInfo.emit(null);

        const registerModel = new RegisterVerificationModel({
            email: email,
        });

        this.apiCustomerService.findCustomerByEmail(registerModel.email).then(
            (customerName: string) => {

                this.emailDuplicated = true;
                this.onSendRegisterInfo.emit(null);

            }, (err) => {

                this.emailDuplicated = false;
                // Si el resto del form ya es valido, notificamos que ahora si.
                this._emitIfValid();
            }
        );

    }

    private _emitIfValid() {

        if (this.registerForm?.valid && !this.emailDuplicated) {
            this.onSendRegisterInfo.emit(this._buildRegisterModel());
        } else {
            this.onSendRegisterInfo.emit(null);
        }
    }

    onSelectGeo(levelKey: string, selectedValue: string): void {

        const selectedId = parseInt(selectedValue, 10);
        const currentLevelIndex = this.geoDataStructure.findIndex(field => field.key === levelKey);

        this.registerForm.get(levelKey)?.setValue(selectedId);

        this.geoDataStructure.slice(currentLevelIndex + 1).forEach((nextField) => {

            if (nextField.ordinal === 2) {

                // Se conserva la opción placeholder (id 0): el control se resetea a 0 y si
                // no existe esa opción el navegador muestra el primer cantón sin disparar
                // (change), dejando el form en 0 e inválido.
                const placeholder = this.geoData[nextField.key][0];

                this.filteredData[nextField.key] = [
                    placeholder,
                    ...this.geoData[nextField.key].filter(
                        x => x.id !== placeholder.id
                            && x.provinceId === this.registerForm.get(this.geoDataStructure[0].key)?.value
                    )
                ];
            }

            this.registerForm.get(nextField.key)?.setValue(0);
        });
    }

    get email() {
        return this._formGet(this.registerForm, 'email');
    }
    get invalidEmail(): boolean {
        if (this.email && this.email.value) {
            this.email.setValue(this.email.value?.trim());
        }
        return this._isInvalid(this.registerForm, 'email', this.touched);
    }

    get name() { return this._formGet(this.registerForm, 'name'); }
    get invalidName(): boolean {
        return this._isInvalid(this.registerForm, 'name', this.touched);
    }

    get lastName() { return this._formGet(this.registerForm, 'lastName'); }
    get invalidLastName(): boolean {
        return this._isInvalid(this.registerForm, 'lastName', this.touched);
    }

    get birthDate() { return this._formGet(this.registerForm, 'birthDate'); }
    get invalidBirthDate(): boolean {
        return this._isInvalid(this.registerForm, 'birthDate', this.touched);
    }

    get genderType() { return this._formGet(this.registerForm, 'genderType'); }
    get invalidGenderType(): boolean {
        return this._isInvalid(this.registerForm, 'genderType', this.touched);
    }

    get idCardType() { return this._formGet(this.registerForm, 'idCardType'); }
    get invalidIdCardType(): boolean {
        return this._isInvalid(this.registerForm, 'idCardType', this.touched);
    }

    get idCard() { return this._formGet(this.registerForm, 'idCard'); }
    get invalidIdCard(): boolean {
        return this._isInvalid(this.registerForm, 'idCard', this.touched);
    }

    onChangeIdCardType(): void {
        this.idCard.setValue('');
        this.idCard.markAsUntouched();
    }

    isInvalidGeo(key: string): boolean {
        return this._isInvalid(this.registerForm, key, this.touched);
    }

    private _formInit(): void {

        this.touched = true;

        const emailValidators = this.showEmail
            ? [Validators.required, Validators.email]
            : [];

        const formGroupConfig: any = {
            email: new UntypedFormControl(
                this.initialEmail || '',
                emailValidators
            ),
        };

        // Con Google el nombre viene de la cuenta y no se pide.
        if (this.showName) {

            formGroupConfig.name = new UntypedFormControl('', [Validators.required]);
            formGroupConfig.lastName = new UntypedFormControl('', [Validators.required]);
        }

        // Campos extra solo en modo completo (ofertas de pago con comunidad, etc.).
        if (!this.minimal) {

            formGroupConfig.birthDate = new UntypedFormControl(
                '',
                [Validators.required, Validators.pattern(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/)]
            );
            formGroupConfig.genderType = new UntypedFormControl(
                0,
                [Validators.required, Validators.min(1)]
            );
            if (this.showIdCard) {

                formGroupConfig.idCardType = new UntypedFormControl(
                    0,
                    [Validators.required, Validators.min(1)]
                );
                formGroupConfig.idCard = new UntypedFormControl(
                    '',
                    [Validators.required, Validators.minLength(6), Validators.maxLength(15)]
                );
            }
        }

        this.geoDataStructure.forEach((field) => {
            formGroupConfig[field.key] = this._geoControl();
        });

        this.registerForm = this.formBuilder.group(formGroupConfig);

        this._applyPrefill();

        // Escuchamos valueChanges (no statusChanges) porque el status no re-emite
        // cuando el form pasa de VALID a VALID tras cambiar solo un valor.
        // Sin debounce porque perdíamos el último cambio cuando el usuario cerraba
        // el form rápido y quedaba deshabilitado el botón.
        this.registerForm.valueChanges.subscribe(() => {
            this._emitIfValid();
        });
    }

    /**
     * Carga lo que la cuenta ya tiene. Se ignoran los 0 y vacíos: el API devuelve
     * "0" para provincia y cantón cuando no están definidos.
     */
    private _applyPrefill(): void {

        const data = this.prefill;

        if (!data) return;

        const setIfPresent = (key: string, value: any) => {

            if (value == null || String(value).trim() === '') return;

            this.registerForm.get(key)?.setValue(value);
        };

        setIfPresent('name', data.name);
        setIfPresent('lastName', data.lastName);
        setIfPresent('idCard', data.idCard);

        if (Number(data.idCardType) > 0) setIfPresent('idCardType', Number(data.idCardType));
        if (Number(data.genderType) > 0) setIfPresent('genderType', Number(data.genderType));

        if (data.birthDate) {

            const date = data.birthDate instanceof Date ? data.birthDate : new Date(data.birthDate);

            if (!isNaN(date.getTime())) {

                const dd = `${date.getDate()}`.padStart(2, '0');
                const mm = `${date.getMonth() + 1}`.padStart(2, '0');

                setIfPresent('birthDate', `${dd}/${mm}/${date.getFullYear()}`);
            }
        }

        // Al setear la provincia hay que filtrar sus cantones, igual que el (change)
        const provinceKey = this.geoDataStructure[0]?.key;
        const cantonKey = this.geoDataStructure[1]?.key;

        if (provinceKey && Number(data.province) > 0) {

            this.onSelectGeo(provinceKey, String(data.province));

            if (cantonKey && Number(data.canton) > 0) {

                const exists = this.filteredData[cantonKey]?.some(x => x.id === Number(data.canton));

                if (exists) this.registerForm.get(cantonKey)?.setValue(Number(data.canton));
            }
        }
    }

    private _buildRegisterModel(): RegisterModel {

        const value = this.registerForm.value;

        // birthDate solo existe en modo completo; en modo mínimo va null.
        let birthDate: Date = null;
        if (value.birthDate) {
            const [day, month, year] = (value.birthDate as string).split('/').map(p => parseInt(p, 10));
            birthDate = new Date(year, month - 1, day);
        }

        const provinceKey = this.geoDataStructure[0]?.key;
        const cantonKey = this.geoDataStructure[1]?.key;

        return new RegisterModel({
            email: (value.email || this.initialEmail || '').toLowerCase().trim(),
            name: value.name,
            lastName: value.lastName,
            birthDate: birthDate,
            genderType: value.genderType ? Number(value.genderType) : null,
            idCardType: value.idCardType ? Number(value.idCardType) as IdCardType : null,
            idCard: value.idCard || null,
            province: provinceKey ? Number(value[provinceKey]) : null,
            canton: cantonKey ? Number(value[cantonKey]) : null,
        });
    }
}
